// src/components/layout/HamburgerMenu.tsx
//
// iSentinel360 v0.4.5 — hamburger menu with "Check for updates" item.
//
// v0.4.5 changes (over v0.4.1):
//   - Added "Check for updates" item in the Settings section. This
//     dispatches the 'is360-check-updates' window event, which
//     UpdatePrompt listens for and triggers BOTH PWA + APK checks.
//   - The item shows a brief "Checking..." state with a spinning
//     icon while the check is in progress. The result feedback
//     (banner appears for PWA or APK, toast for "up to date" /
//     "update available" / "error") is rendered by UpdatePrompt.
//
// All other behavior is preserved exactly as in v0.4.1:
//   - Offline `disabled` state (the menu trigger is greyed out
//     when the user is offline)
//   - Role-based nav item filtering
//   - Civil respondent type narrowing (law enforcement vs emergency)
//   - Portal rendering so the panel escapes Header stacking context
//   - Body scroll lock when open
//   - Escape-key closing
//   - Active-route highlighting
//   - Logout flow

import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Menu, X, Shield, LayoutDashboard, Building, Network,
  Settings as SettingsIcon, LogOut, ChevronRight, RefreshCw,
  FileText, FileSearch, AlertOctagon, Car, FolderCog,
  Download,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useFirstInstall } from '../../hooks/useFirstInstall';
import { cn } from '../../lib/utils';

type Role = 'admin' | 'guard' | 'cadmin' | 'superadmin' | 'sadmin' | 'tadmin' | 'agent' | 'agentAdmin';

interface NavItem {
  to: string;
  // i18n key, resolved at render time via t() (the array is module-level
  // so it can't call useTranslation directly).
  labelKey: string;
  icon: typeof Shield;
  visibleTo: ReadonlyArray<Role>;
}

const NAV_ITEMS: ReadonlyArray<NavItem> = [
  // Commercial / existing routes
  { to: '/guard',   labelKey: 'nav.guard',   icon: Shield,          visibleTo: ['admin', 'guard', 'cadmin', 'superadmin', 'sadmin', 'tadmin'] },
  { to: '/admin',   labelKey: 'nav.admin',   icon: LayoutDashboard, visibleTo: ['admin', 'cadmin', 'superadmin', 'sadmin'] },
  { to: '/client',  labelKey: 'nav.client',  icon: Building,        visibleTo: ['cadmin', 'superadmin', 'sadmin'] },
  { to: '/network', labelKey: 'nav.network', icon: Network,         visibleTo: ['superadmin', 'sadmin', 'tadmin'] },
  { to: '/opsec#overview', labelKey: 'nav.opsec', icon: Shield, visibleTo: ['superadmin', 'sadmin'] },

  // Civil respondent — user-facing (entry + history)
  { to: '/civil-respondent',         labelKey: 'nav.civilRespond',     icon: Shield,     visibleTo: ['agent', 'agentAdmin', 'superadmin', 'sadmin'] },
  { to: '/civil-respondent/actions', labelKey: 'nav.civilActions',     icon: FileText,   visibleTo: ['agent', 'agentAdmin', 'superadmin', 'sadmin'] },

  // Civil respondent — admin (manage the 3 databases). agentAdmin only.
  { to: '/missing-persons', labelKey: 'nav.missingPersons', icon: FileSearch,  visibleTo: ['agentAdmin', 'superadmin', 'sadmin'] },
  { to: '/most-wanted',     labelKey: 'nav.mostWanted',     icon: AlertOctagon,visibleTo: ['agentAdmin', 'superadmin', 'sadmin'] },
  { to: '/stolen-vehicles', labelKey: 'nav.stolenVehicles', icon: Car,         visibleTo: ['agentAdmin', 'superadmin', 'sadmin'] },
];

// =====================================================================
// v0.4.1 — HamburgerMenu accepts a `disabled` prop.
//
// When `disabled` is true (the user is offline), the trigger
// button is greyed out, the menu won't open, and the user gets a
// tooltip explaining why. The menu body, NAV_ITEMS, slide-in
// panel, role-based filtering, civil respondent type narrowing,
// portal rendering, body scroll lock, escape-key handling,
// active-route highlighting, and the logout flow are all
// UNCHANGED — those are yours and stay yours.
//
// The Header passes `disabled={!isOnline}` to this component.
// The trigger guard is belt-and-braces: the `disabled` attribute
// on the <button> already prevents the click, and the early
// return in the onClick is a second line of defense in case
// the button somehow gets clicked (e.g. via keyboard).
// =====================================================================
export default function HamburgerMenu({ disabled = false }: { disabled?: boolean }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // v0.4.5: brief "Checking..." state for the update check item.
  // Mirrors what UpdatePrompt shows in its toast — they're
  // synchronized via the 'is360-check-updates' event.
  const [isChecking, setIsChecking] = useState(false);
  // v0.4.6: PWA-as-installer — when the user is on Android mobile
  // but not yet in the APK, the menu shows a prominent "Install the
  // app" item that downloads the latest APK. The same install URL
  // is also surfaced via the FirstInstallBanner at the top of every
  // page. The menu item is a redundant entry point for users who
  // dismissed the banner but later decided to install.
  const firstInstall = useFirstInstall();

  const close = useCallback(() => setOpen(false), []);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  // Close whenever the route changes (so navigation feels natural).
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock body scroll while the panel is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const handleLogout = () => {
    close();
    logout();
    navigate('/login', { replace: true });
  };

  // v0.4.5: dispatch the manual update check. UpdatePrompt listens
  // for this event and runs BOTH the PWA SW re-check and the APK
  // /api/apk-version hit. The result feedback (banner / toast) is
  // rendered by UpdatePrompt, not here — this component just
  // signals "user asked to check".
  const handleCheckUpdates = () => {
    setIsChecking(true);
    // Close the menu FIRST so the user sees the result banner/toast
    // on the underlying page, not behind the dimmed menu overlay.
    // (Without this, the "Checking..." toast and the eventual
    // update-available banner render behind the menu and the user
    // has to tap the X before they see anything.)
    close();
    window.dispatchEvent(new CustomEvent('is360-check-updates'));
    // Reset the local "Checking..." state after a reasonable timeout.
    // The actual check (and result feedback) is handled by UpdatePrompt.
    setTimeout(() => setIsChecking(false), 3000);
  };

  const role = user?.role as Role | undefined;
  // Civil respondent org type. Emergency Services orgs should NOT see
  // Most Wanted or Stolen Vehicles — those are law-enforcement-only.
  // We cast through `any` because the AuthContext User type doesn't
  // formally declare `civil_respondent_type` yet (it comes from the
  // backend's login response). Law Enforcement → all 3, Emergency
  // Services → only Missing Persons. SuperAdmin/SAdmin see all (no
  // change from previous behavior).
  const civilRespondentType = (user as any)?.civil_respondent_type as 'law_enforcement' | 'emergency_services' | undefined;
  const visibleItems = role
    ? NAV_ITEMS.filter(i => {
        if (!i.visibleTo.includes(role)) return false;
        // Civil admin (agentAdmin) — narrow by org type. Law enforcement
        // gets all 3 (Missing / Most Wanted / Stolen Vehicles);
        // emergency services gets only Missing Persons. The
        // superadmin/sadmin branches fall through unchanged.
        if (role === 'agentAdmin' && civilRespondentType === 'emergency_services') {
          return i.to !== '/most-wanted' && i.to !== '/stolen-vehicles';
        }
        return true;
      })
    : [];

  const userName = user?.name || user?.email?.split('@')[0] || '';
  const userEmail = user?.email || '';

  // Tooltip text — when disabled, prefer an i18n key if the user
  // has it, otherwise fall back to a clear English string.
  const triggerTitle = disabled
    ? (t('common.offlineMenuDisabled') || 'Menu unavailable while offline')
    : (t('nav.menu') || 'Menu');

  return (
    <>
      {/* Trigger button. */}
      <button
        type="button"
        onClick={() => { if (disabled) return; setOpen(true); }}
        disabled={disabled}
        aria-disabled={disabled}
        className={cn(
          "p-2 rounded-md hover:bg-accent transition-colors",
          // When offline, greyed out + not-allowed cursor + no
          // hover effect. Keeps the icon visible so the user
          // understands the menu is paused, not removed.
          disabled && "opacity-50 cursor-not-allowed hover:bg-transparent"
        )}
        title={triggerTitle}
        aria-label={triggerTitle}
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Backdrop + slide-in panel.
          Rendered via a Portal at document.body so it escapes any parent
          stacking context (the Header is sticky + backdrop-blur, which
          creates its own context — without the portal the panel would
          be trapped at the header's z-index level and appear under
          other elements). */}
      {open && createPortal(
        <div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true">
          {/* Dimmed background, click to close. */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={close}
            aria-hidden="true"
          />

          {/* Right-anchored slide-in panel. */}
          <aside
            className={cn(
              "absolute top-0 right-0 h-full w-72 sm:w-80",
              "bg-background border-l shadow-xl",
              "flex flex-col"
            )}
            onClick={e => e.stopPropagation()}
          >
            {/* Header: user info + close button. */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{userName || ' '}</p>
                <p className="text-xs text-muted-foreground truncate">{userEmail}</p>
                {/* Cast to `any` for `user_type` — the auth User interface
                    in ../../context/AuthContext.tsx doesn't declare this
                    field but the backend JWT includes it. Long-term fix:
                    add `user_type?: string` to the User interface in
                    AuthContext.tsx and drop the cast. */}
                {(user as any)?.user_type === 'civil_respondent' && (
                  <p className="text-[10px] text-green-600 mt-0.5">Civil respondent</p>
                )}
              </div>
              <button
                type="button"
                onClick={close}
                className="p-2 rounded-md hover:bg-accent"
                title={t('common.close') || 'Close'}
                aria-label={t('common.close') || 'Close'}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Nav items. */}
            <nav className="flex-1 overflow-y-auto p-2">
              <ul className="space-y-1">
                {visibleItems.map(item => {
                  const Icon = item.icon;
                  const active = location.pathname === item.to;
                  return (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        onClick={close}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                          active
                            ? "bg-primary text-primary-foreground"
                            : "hover:bg-accent text-foreground"
                        )}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="flex-1">{t(item.labelKey)}</span>
                        {active && <ChevronRight className="w-4 h-4 opacity-70" />}
                      </Link>
                    </li>
                  );
                })}
              </ul>

              {/* Settings — visible to every authenticated user. */}
              <div className="mt-4 pt-4 border-t">
                <Link
                  to="/settings"
                  onClick={close}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                    location.pathname === '/settings'
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-accent text-foreground"
                  )}
                >
                  <SettingsIcon className="w-4 h-4" />
                  <span className="flex-1">{t('nav.settings') || 'Settings'}</span>
                  {location.pathname === '/settings' && <ChevronRight className="w-4 h-4 opacity-70" />}
                </Link>

                {/* v0.4.5: "Check for updates" item. Dispatches the
                    'is360-check-updates' event; UpdatePrompt handles the
                    actual checks + result feedback. The local
                    isChecking state shows a spinning icon and "Checking…"
                    label for 3s, then resets. This matches the duration
                    the toast in UpdatePrompt is visible. */}
                <button
                  type="button"
                  onClick={handleCheckUpdates}
                  disabled={isChecking}
                  className={cn(
                    "flex w-full items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                    "hover:bg-accent text-foreground",
                    isChecking && "opacity-60 cursor-wait"
                  )}
                >
                  <RefreshCw
                    className={cn("w-4 h-4", isChecking && "animate-spin")}
                  />
                  <span className="flex-1 text-left">
                    {isChecking
                      ? (t('update.checking') || 'Checking…')
                      : (t('nav.checkUpdates') || 'Check for updates')}
                  </span>
                </button>

                {/* v0.4.6: PWA-as-installer menu item. Only shown
                    when the user is on Android mobile but not in
                    the APK yet. Tapping it triggers a regular
                    browser download of the latest APK (the same
                    flow as the FirstInstallBanner). The user then
                    opens the APK from the download notification to
                    install. */}
                {firstInstall.shouldShow && firstInstall.apkInfo && (
                  <a
                    href={firstInstall.apkInfo.downloadUrl}
                    download
                    onClick={close}
                    className="flex w-full items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-950/50"
                  >
                    <Download className="w-4 h-4" />
                    <span className="flex-1 text-left">
                      {t('install.menuItem', {
                        version: firstInstall.apkInfo.versionName,
                      })}
                    </span>
                  </a>
                )}
              </div>
            </nav>

            {/* Footer: logout. */}
            <div className="p-2 border-t">
              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center gap-3 px-3 py-2 rounded-md text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="flex-1 text-left">{t('common.logout') || 'Logout'}</span>
              </button>
              <p className="text-[10px] text-muted-foreground text-center mt-2">
                v.{__APP_VERSION__}
              </p>
            </div>
          </aside>
        </div>,
        document.body
      )}
    </>
  );
}
